/**
 * Per-test results, streaming in as the run proceeds.
 *
 * Parsed from the `list` reporter's own output rather than waiting for the JSON
 * report at the end. That matters: a full regression is 25-40 minutes, and until
 * now the only thing to look at during it was a scrolling log. A QA engineer could
 * not tell whether the run was going well until it was over.
 *
 * Parsing stdout is admittedly a bit crude. The alternative — a custom reporter
 * posting events back to the console — is cleaner but needs the runner to reach the
 * console over the network, and the container is deliberately isolated. The list
 * reporter's format is stable across Playwright versions and a parse miss simply
 * means a row does not appear, never a wrong result.
 */
import { useMemo, useState } from 'react';
import { TestDiagnostics } from './TestDiagnostics';
import type { LogLine } from '../api';

export interface LiveTest {
  status: 'passed' | 'failed' | 'skipped' | 'running';
  index: number;
  title: string;
  /** The describe path, shown small so the title stays readable. */
  suite: string;
  file: string;
  durationMs?: number;
  retry: boolean;
  /** How many completed attempts this test had. >1 means it was retried. */
  attempts: number;
  /** Passed, but only after a retry. Reported separately: this is NOT green. */
  unstable: boolean;
  /** Which application/repository the test belongs to — where to go and debug. */
  app: string;
  /** When the result was reported, from the log line's own timestamp. */
  at?: string;
}

/**
 * The repository a spec belongs to, from its path.
 *
 * Without this a failure says "auth.spec.ts" and the person reading it has to know
 * the layout to work out which codebase to open. The console knows; it should say.
 */
export function appOf(file: string): string {
  const m = /automation\/specs\/([a-z0-9-]+)\//.exec(file) ?? /automation\/(api)\//.exec(file);
  const key = m?.[1] ?? '';
  return {
    player: 'PLAYER',
    studio: 'STUDIO',
    landing: 'landing-page',
    docs: 'docs',
    marketplace: 'marketplace',
    journeys: 'cross-app',
    smoke: 'platform',
    discovery: 'discovery',
    console: 'QA Console',
    api: 'CAIT API',
  }[key] ?? '';
}

/**
 *   ✓  12 [chromium] › specs/studio/auth.spec.ts:30:7 › STUDIO auth › signs in (1.2s)
 *   ✘  13 [chromium] › ... (retry #1) (3.4s)
 *   -  14 [chromium] › ... skipped
 */
const LINE = /^\s*(✓|✔|✘|✕|×|-|›)?\s*(\d+)\s+\[([^\]]+)\]\s+›\s+(\S+?)(?::\d+:\d+)?\s+›\s+(.+?)(?:\s+\((\d+(?:\.\d+)?)(ms|s|m)\))?\s*$/;

/**
 * Jest and pytest, for Unit runs.
 *
 *   PASS src/__tests__/hooks/useUser.test.ts
 *   PASS src/__tests__/components/Courses/CourseGridCard.test.tsx (14.985 s)
 *   FAIL src/__tests__/lib/thing.test.ts
 *   tests/unit/test_course.py::test_creates_course PASSED
 *
 * Without this the Results panel stayed empty for the whole of a Unit run — the
 * operator could see hundreds of lines scrolling past and no summary, which is
 * exactly the thing the panel exists to prevent.
 */
const JEST_LINE = /^\s*(PASS|FAIL)\s+(\S+)(?:\s+\((\d+(?:\.\d+)?)\s*s\))?\s*$/;
const PYTEST_LINE = /^\s*(\S+\.py)::(\S+)\s+(PASSED|FAILED|SKIPPED|ERROR)/;

/**
 * k6, for Performance runs.
 *
 *   [k6] PASS courses-list 143ms
 *   [k6] FAIL course-detail status=502 1201ms
 *   [k6] THRESHOLD-PASS http_req_duration p(95)<1200 p(95)=412ms
 *   [k6] THRESHOLD-FAIL checks rate>0.99 rate=0.9712
 *   [k6] SUMMARY 2 budget(s) breached — this run FAILS
 *
 * This is OUR format, emitted by performance/api-load.js, not k6's. k6 reports a
 * redrawing progress bar and an end-of-test summary; neither streams per-check
 * results a parser can follow, and the Results panel exists precisely so that a
 * run in progress is legible. Both ends of the format live in this repository.
 *
 * Sampled lines mean the panel shows a representative stream rather than every
 * request — at 5 req/s across five scenarios, one line per request would be
 * thousands of rows nobody reads.
 */
/** Load and the scan are both aimed at the CAIT API — that is where a finding sends someone. */
const K6_APP = 'CAIT API';
const ZAP_APP = 'CAIT API';

const K6_LINE = /^\s*\[k6\]\s+(PASS|FAIL)\s+(\S+)\s+(.*?)\s*$/;
const K6_THRESHOLD_LINE = /^\s*\[k6\]\s+THRESHOLD-(PASS|FAIL)\s+(\S+)\s+(\S+)\s*(.*?)\s*$/;
const K6_SUMMARY_LINE = /^\s*\[k6\]\s+SUMMARY\s+(.*?)\s*$/;

function parseK6Results(lines: LogLine[]): LiveTest[] {
  const out: LiveTest[] = [];
  // Sampled request lines are collapsed per endpoint: the last observation wins and
  // a single failure keeps the row red, so one bad response is not scrolled away by
  // the fifty good ones after it.
  const byEndpoint = new Map<string, LiveTest>();

  for (const line of lines) {
    const threshold = K6_THRESHOLD_LINE.exec(line.text);
    if (threshold) {
      const [, verdict, metric, expression, actual] = threshold;
      // Budgets are appended in order rather than keyed: each is a distinct
      // assertion and they arrive once, at the end.
      out.push({
        status: verdict === 'PASS' ? 'passed' : 'failed',
        index: 10_000 + out.length,
        title: `${metric} ${expression}${actual ? ` — ${actual}` : ''}`,
        suite: 'budget',
        file: 'performance/api-load.js',
        retry: false,
        // k6 does not retry: an arrival-rate run measures what happened, and
        // re-running a request would distort the very timing being measured.
        attempts: 1,
        unstable: false,
        app: K6_APP,
      });
      continue;
    }

    const summary = K6_SUMMARY_LINE.exec(line.text);
    if (summary) {
      out.push({
        status: /breached|FAILS/.test(summary[1]) ? 'failed' : 'passed',
        index: 20_000,
        title: summary[1],
        suite: 'summary',
        file: 'performance/api-load.js',
        retry: false,
        attempts: 1,
        unstable: false,
        app: K6_APP,
      });
      continue;
    }

    const request = K6_LINE.exec(line.text);
    if (!request) continue;

    const [, verdict, endpoint, detail] = request;
    const existing = byEndpoint.get(endpoint);
    const failed = verdict === 'FAIL' || existing?.status === 'failed';
    const ms = /(\d+)ms\b/.exec(detail);

    byEndpoint.set(endpoint, {
      status: failed ? 'failed' : 'passed',
      index: existing?.index ?? byEndpoint.size + 1,
      title: endpoint,
      suite: detail.replace(/\s*\d+ms\b/, '').trim() || 'load',
      file: 'performance/api-load.js',
      durationMs: ms ? Number(ms[1]) : undefined,
      retry: false,
      attempts: 1,
      unstable: false,
      app: K6_APP,
    });
  }

  return [...byEndpoint.values(), ...out];
}

/**
 * OWASP ZAP, for Security runs.
 *
 *   [zap] FINDING High SQL Injection @ POST /api/v1/courses/ — id=1' OR '1'='1
 *   [zap] NOTE Low Server leaks version @ GET /api/v1/users/me — nginx/1.24.0
 *   [zap] SESSION-OK the scan was still authenticated when it finished
 *   [zap] GATE fails at High or above
 *   [zap] SUMMARY 1 finding(s) at or above High — this run FAILS
 *
 * FINDING is at or above the gate and fails the run; NOTE is below it and is
 * reported without failing — the distinction is made by report.mjs, not here, so
 * the panel cannot disagree with the exit code.
 */
const ZAP_FINDING_LINE = /^\s*\[zap\]\s+(FINDING|NOTE)\s+(\S+)\s+(.*?)\s+@\s+(.*?)\s*$/;
const ZAP_SESSION_LINE = /^\s*\[zap\]\s+SESSION-(OK|LOST)\s+(.*?)\s*$/;
const ZAP_SUMMARY_LINE = /^\s*\[zap\]\s+(?:SUMMARY|GATE)\s+(.*?)\s*$/;

function parseZapResults(lines: LogLine[]): LiveTest[] {
  const out: LiveTest[] = [];

  for (const line of lines) {
    const finding = ZAP_FINDING_LINE.exec(line.text);
    if (finding) {
      const [, verdict, severity, name, location] = finding;
      out.push({
        status: verdict === 'FINDING' ? 'failed' : 'skipped',
        index: out.length + 1,
        title: `${severity} · ${name}`,
        suite: location,
        file: 'security/zap-plan.yaml',
        retry: false,
        attempts: 1,
        unstable: false,
        app: ZAP_APP,
        at: line.at,
      });
      continue;
    }

    const session = ZAP_SESSION_LINE.exec(line.text);
    if (session) {
      // A lost session is the failure that would otherwise pass as a clean scan, so
      // it gets its own row rather than being buried in the log.
      out.push({
        status: session[1] === 'OK' ? 'passed' : 'failed',
        index: 30_000,
        title: session[2],
        suite: 'session',
        file: 'security/report.mjs',
        retry: false,
        attempts: 1,
        unstable: false,
        app: ZAP_APP,
        at: line.at,
      });
      continue;
    }

    const summary = ZAP_SUMMARY_LINE.exec(line.text);
    if (summary) {
      out.push({
        status: /FAILS/.test(summary[1]) ? 'failed' : 'passed',
        index: 40_000 + out.length,
        title: summary[1],
        suite: 'summary',
        file: 'security/report.mjs',
        retry: false,
        attempts: 1,
        unstable: false,
        app: ZAP_APP,
        at: line.at,
      });
    }
  }

  return out;
}

function parseUnitResults(lines: LogLine[]): LiveTest[] {
  const byKey = new Map<string, LiveTest>();

  for (const line of lines) {
    const jest = JEST_LINE.exec(line.text);
    if (jest) {
      const [, mark, file, seconds] = jest;
      // A Jest "test" here is a FILE — that is the granularity Jest reports as it
      // goes. The per-assertion counts only arrive in the summary at the end.
      byKey.set(file, {
        status: mark === 'PASS' ? 'passed' : 'failed',
        index: byKey.size + 1,
        title: file.split('/').pop() ?? file,
        suite: file.split('/').slice(0, -1).join('/'),
        file,
        durationMs: seconds ? Number(seconds) * 1000 : undefined,
        retry: false,
        attempts: 1,
        unstable: false,
        app: 'unit',
      });
      continue;
    }

    const py = PYTEST_LINE.exec(line.text);
    if (py) {
      const [, file, test, outcome] = py;
      byKey.set(`${file}::${test}`, {
        status:
          outcome === 'PASSED' ? 'passed'
          : outcome === 'SKIPPED' ? 'skipped'
          : 'failed',
        index: byKey.size + 1,
        title: test,
        suite: file,
        file,
        retry: false,
        attempts: 1,
        unstable: false,
        app: 'unit',
      });
    }
  }

  return [...byKey.values()];
}

export function parseResults(lines: LogLine[]): LiveTest[] {
  /**
   * Keyed by the TEST, not by the reporter's line index.
   *
   * Playwright prints every attempt on its own numbered line, so keying by index
   * turned one retried test into three rows — two red "✕ … retried" plus a green
   * one. An operator reading that reasonably concluded three tests had failed when
   * one test had been retried and passed. Attempts are folded into a single row
   * whose status is the FINAL attempt's, marked `unstable` when it only got there
   * after a retry.
   */
  const byTest = new Map<string, LiveTest>();

  for (const line of lines) {
    const m = LINE.exec(line.text);
    if (!m) continue;

    const [, mark, index, , file, rest, amount, unit] = m;
    const retry = /\(retry #\d+\)/.test(rest);

    // Real output is `describe › test title`, with tags in the describe:
    //   "docs smoke @docs @smoke › / renders 4 sections from pages/index.mdx"
    // The last segment is the test; the rest is context, and the tags are noise in
    // a narrow panel.
    const cleaned = rest.replace(/\s*\(retry #\d+\)\s*/, '').trim();
    const segments = cleaned.split(' › ').map((x) => x.trim()).filter(Boolean);
    const title = (segments[segments.length - 1] ?? cleaned).replace(/@[a-z0-9-]+/gi, '').trim();
    const suite = segments.slice(0, -1).join(' › ').replace(/@[a-z0-9-]+/gi, '').trim();

    const ms = amount
      ? unit === 'ms' ? Number(amount)
        : unit === 'm' ? Number(amount) * 60_000
        : Number(amount) * 1000
      : undefined;

    const status: LiveTest['status'] =
      mark === '✓' || mark === '✔' ? 'passed'
      // Every cross glyph, not just the one this Playwright version happens to emit:
      // a line whose mark is unrecognised is dropped entirely, so a failure would
      // vanish from the panel rather than being shown wrongly.
      : mark === '✘' || mark === '✕' || mark === '×' ? 'failed'
      : mark === '-' ? 'skipped'
      : 'running';

    // The list reporter prints a line when a test STARTS and again when it ends,
    // so a later line for the same index supersedes the earlier one.
    const key = `${file}::${suite}::${title}`;
    const previous = byTest.get(key);
    // Only a line carrying a mark is a completed attempt; the reporter also prints
    // a line when a test STARTS.
    const attempts = (previous?.attempts ?? 0) + (mark && mark !== '›' ? 1 : 0);

    byTest.set(key, {
      status,
      index: Math.max(previous?.index ?? 0, Number(index)),
      title,
      suite,
      file,
      durationMs: ms ?? previous?.durationMs,
      retry: retry || (previous?.retry ?? false),
      attempts,
      // Passed only because it was retried. Surfaced as its own state because
      // Playwright counts it as a pass, and that is how a flaky test survives for
      // months while every run looks green.
      unstable: status === 'passed' && attempts > 1,
      app: appOf(file),
      at: line.at ?? previous?.at,
    });
  }

  return [...byTest.values()].sort((a, b) => b.index - a.index);
}

export function LiveResults({ lines, finished, runId }: { lines: LogLine[]; finished: boolean; runId?: string }) {
  // Playwright first; Unit, Performance and Security each produce none of the
  // others' lines, so each shape is tried in turn rather than showing an empty panel
  // for the whole of a run that is reporting perfectly well in another format.
  const tests = useMemo(() => {
    const playwright = parseResults(lines);
    if (playwright.length) return playwright;
    const unit = parseUnitResults(lines);
    if (unit.length) return unit;
    const load = parseK6Results(lines);
    if (load.length) return load;
    return parseZapResults(lines);
  }, [lines]);
  // Which row is open. Only one at a time: the panel is tall, and comparing two
  // failures side by side is not what anyone does here.
  const [expanded, setExpanded] = useState<number | null>(null);

  const counts = useMemo(() => ({
    passed: tests.filter((t) => t.status === 'passed').length,
    failed: tests.filter((t) => t.status === 'failed').length,
    skipped: tests.filter((t) => t.status === 'skipped').length,
  }), [tests]);

  const lastAt = useMemo(
    () => tests.map((t) => t.at).filter(Boolean).sort().pop(),
    [tests],
  );

  // Failures first while the run is live — that is what someone watching wants to
  // see the moment it happens, not buried under fifty passes.
  const ordered = useMemo(() => {
    const rank = (t: LiveTest) => (t.status === 'failed' ? 0 : t.status === 'running' ? 1 : 2);
    return [...tests].sort((a, b) => rank(a) - rank(b) || b.index - a.index);
  }, [tests]);

  return (
    // A REAL fixed height, not "whatever the grid row ends up being": a grid's
    // `stretch` only equalises siblings to the row's own auto-computed height,
    // which is the tallest sibling's CONTENT height — so a long, uncapped list
    // here made the whole row (Log included) grow with it instead of scrolling.
    // `h-[28rem]` matches the Log panel next to it, so 600 tests scroll inside a
    // box the same size as everything else, rather than stretching the page.
    <div className="surface flex h-[28rem] flex-col overflow-hidden" data-testid="live-results">
      <div className="flex items-center gap-3 px-4 py-2" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
        <span className="text-sm font-medium">Results</span>

        {tests.length > 0 ? (
          <span className="flex items-center gap-2 text-xs tabular">
            {counts.passed > 0 && <span className="text-pass">{counts.passed} passed</span>}
            {counts.failed > 0 && <span className="text-fail">{counts.failed} failed</span>}
            {counts.skipped > 0 && (
              <span style={{ color: 'hsl(var(--text-faint))' }}>{counts.skipped} skipped</span>
            )}
          </span>
        ) : (
          <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
            {finished ? 'no results reported' : 'waiting for the first test…'}
          </span>
        )}

        {tests.length > 0 && (
          <span className="ml-auto flex items-center gap-1.5 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
            {finished ? (
              <>
                {/* Stated explicitly. "No live dot" is not an answer to "did it
                    finish?" for someone returning to the tab half an hour later. */}
                run finished · {tests.length} test{tests.length === 1 ? '' : 's'} reported
                {lastAt && ` · last at ${new Date(lastAt).toLocaleTimeString()}`}
              </>
            ) : (
              <>
                <span className="dot dot-live" /> running · {tests.length} reported so far
              </>
            )}
          </span>
        )}
      </div>

      {/* `flex-1`, not a fixed `max-h-*`: the surrounding grid stretches this panel
          to match the Log panel's height, and a capped max-height left the list
          filling only its own content up to that cap while the stretched box left
          real empty space below it. `min-h-0` is required alongside `flex-1` here —
          a flex child's default min-height is `auto`, which lets it grow past its
          flex basis and overflow the parent instead of scrolling internally. */}
      <ul className="min-h-0 flex-1 overflow-auto">
        {ordered.map((t) => (
          <li
            key={t.index}
            style={{ borderBottom: '1px solid hsl(var(--border) / 0.5)' }}
          >
          <div
            className={`flex items-baseline gap-2.5 px-4 py-1.5 text-sm ${runId ? 'cursor-pointer hover:opacity-80' : ''}`}
            onClick={() => runId && setExpanded(expanded === t.index ? null : t.index)}
            data-testid={`result-row-${t.index}`}
          >
            <span
              className="w-4 shrink-0 text-center"
              style={{
                color:
                  t.unstable ? 'hsl(var(--warn))'
                  : t.status === 'passed' ? 'hsl(var(--pass))'
                  : t.status === 'failed' ? 'hsl(var(--fail))'
                  : t.status === 'running' ? 'hsl(var(--accent))'
                  : 'hsl(var(--text-faint))',
              }}
            >
              {t.unstable ? '!' : t.status === 'passed' ? '✓' : t.status === 'failed' ? '✕' : t.status === 'skipped' ? '–' : '·'}
            </span>

            {/* Which codebase to open. */}
            <span
              className="w-24 shrink-0 truncate text-xs"
              style={{ color: 'hsl(var(--text-faint))' }}
              title={t.file}
            >
              {t.app}
            </span>

            <span className="flex-1 truncate" title={`${t.file} — ${t.suite} › ${t.title}`}>
              {t.suite && (
                <span className="mr-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                  {t.suite}
                </span>
              )}
              {t.title}
              {t.unstable && (
                <span className="ml-2 text-xs text-warn" title={`passed on attempt ${t.attempts}`}>
                  unstable — passed only after {t.attempts - 1} retr{t.attempts - 1 === 1 ? 'y' : 'ies'}
                </span>
              )}
            </span>

            <span className="w-14 shrink-0 text-right text-xs tabular" style={{ color: 'hsl(var(--text-faint))' }}>
              {t.durationMs !== undefined ? `${(t.durationMs / 1000).toFixed(1)}s` : ''}
            </span>

            {/* Wall-clock time the result landed, so progress is readable after the
                fact — "which tests ran before it stalled?" needs a clock, not a
                duration. */}
            <span className="w-16 shrink-0 text-right text-xs tabular" style={{ color: 'hsl(var(--text-faint))' }}>
              {t.at ? new Date(t.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
            </span>

            {runId && (
              <span className="w-4 shrink-0 text-center text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                {expanded === t.index ? '▾' : '▸'}
              </span>
            )}
          </div>

          {/* Expandable while the run is still going. The network log and steps are
              collected from the container when it exits, so mid-run this shows what
              the test IS and where it lives — which is the question someone actually
              has when a name means nothing to them. */}
          {runId && expanded === t.index && (
            <TestDiagnostics
              runId={runId}
              testTitle={t.title}
              test={t}
              runFinished={finished}
            />
          )}
          </li>
        ))}

        {ordered.length === 0 && (
          <li className="px-4 py-6 text-center text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
            Tests will appear here as they finish.
          </li>
        )}
      </ul>
    </div>
  );
}
